import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { Dispatch, FC, ReactNode, SetStateAction, useState } from 'react';
import { DropdownItemModel } from '../ExitSlip/ExitSlipModalFilters';
import { Class } from '../../models/Class';
import { availableOptionsInitialState, AvailablesOptions, FormState, SearchItemsType } from '../../pages/SingleUnit';
import { isAllSelected } from '../../businessLogicService/singleUnitFilters';
import { sortClassesList } from '../../utils/sortClassesList';
import { formatClassDisplayName } from '../../utils/formatClassDisplayName';
import { useAppDispatch } from '../../redux/hooks';
import { setTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { setUnit } from '../../redux/reducers/unitSlice';

interface Props {
  miamiBrand: boolean;
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  availablesOptions: AvailablesOptions;
  optionsList: AvailablesOptions;
  standardOptions: DropdownItemModel[];
  unitEnabled: boolean;
  optionalFiltersEnabled: boolean;
}

export interface FiltteredOptions {
  roadmap: DropdownItemModel[];
  unit: DropdownItemModel[];
  lesson: DropdownItemModel[]; // optional
  class: Class[];
}

const SingleUnitFilter: FC<Props> = ({
  miamiBrand,
  form,
  setForm,
  availablesOptions,
  optionsList,
  standardOptions,
  unitEnabled,
  optionalFiltersEnabled,
}) => {
  const [filteredOptions, setFilteredOptions] = useState<FiltteredOptions>(availableOptionsInitialState);
  const dispatch = useAppDispatch();

  const handleChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // For Roadmap
  const handleRoadmapChange = (value: DropdownItemModel) => {
    handleChange('roadmap', value);
    dispatch(setTargetGroup(value?.value ? { id: value.value, name: value.label } : undefined));
  };

  // For Unit
  const handleUnitChange = (value: DropdownItemModel) => {
    handleChange('unit', value);
    dispatch(setUnit(value?.value ? { id: value.value, name: value.label } : undefined));
  };

  const searchItems = (event: AutoCompleteCompleteEvent, itemList: DropdownItemModel[], type: SearchItemsType) => {
    let _filteredItems;
    if (!event.query.trim().length) {
      _filteredItems = [...itemList];
    } else {
      _filteredItems = itemList.filter((x: DropdownItemModel) => {
        return x.label.toLowerCase().includes(event.query.toLowerCase());
      });
    }

    if (type === SearchItemsType.ROADMAP) setFilteredOptions({ ...filteredOptions, roadmap: _filteredItems });
    if (type === SearchItemsType.UNIT) setFilteredOptions({ ...filteredOptions, unit: _filteredItems });
  };
  const classItemTemplate = (cls: Class) => (cls ? formatClassDisplayName(cls) : '');

  const pill = (label: string, content: ReactNode, active: boolean) => (
    <div
      className={`nc_unit-filter-pill${active ? ' nc_unit-filter-pill-active' : ''}${
        optionalFiltersEnabled ? '' : ' p-disabled'
      } ${miamiBrand ? 'tw-w-[33%]' : 'tw-w-[50%]'} `}
    >
      <span className="nc_unit-filter-pill-label">{label}:</span>
      {content}
    </div>
  );

  /** A filter reads as active once it narrows something; the neutral "All" state does not. */
  const isNarrowed = (selectedIds: string[], options: DropdownItemModel[]) =>
    selectedIds.length > 0 && !isAllSelected(selectedIds, options);

  const multiSelectProps = (field: 'lesson' | 'standard', options: DropdownItemModel[]) => ({
    value: form[field],
    options,
    optionLabel: 'label',
    optionValue: 'value',
    filter: true,
    showClear: true,
    disabled: !optionalFiltersEnabled,
    // The clearable marker drives the Figma's chevron-to-× swap, which Class does not get.
    className: 'nc_unit-filter-multiselect nc_unit-filter-multiselect-clearable',
    // PrimeReact composes the label itself: up to maxSelectedLabels it joins the names, beyond that it
    // falls back to selectedItemsLabel — substituting {0} for the count, or printing it verbatim when
    // there is no placeholder to substitute, which is how a full selection reads "All".
    // An empty selection is the other face of the same neutral state: asNeutralSelection collapses both
    // to "no filter", so the placeholder reads "All" too and the header's select-all toggle can clear.
    placeholder: 'All',
    maxSelectedLabels: 1,
    selectedItemsLabel: isAllSelected(form[field], options) ? 'All' : '{0} selected',
    // These lists run to ~40 standards on average and 157 at the extreme.
    virtualScrollerOptions: { itemSize: 40 },
    onChange: (e: MultiSelectChangeEvent) => handleChange(field, e.value),
  });

  const requiredFilters = (
    <div className="nc_unit-filter-required">
      <div className="nc_unit-filter-field">
        <label className="nc_unit-filter-field-label" htmlFor="roadmap">
          Roadmap <span className="nc_unit-filter-required-mark">*</span>
        </label>
        <AutoComplete
          dropdown
          inputId="roadmap"
          className="nc_unit-filter-autocomplete"
          placeholder="Select a Roadmap"
          value={form.roadmap}
          suggestions={filteredOptions.roadmap}
          completeMethod={e => searchItems(e, optionsList.roadmap, SearchItemsType.ROADMAP)}
          onChange={(e: AutoCompleteChangeEvent) => handleRoadmapChange(e.value)}
          field="label"
        />
      </div>
      <div className={`nc_unit-filter-field${unitEnabled ? '' : ' p-disabled'}`}>
        <label className="nc_unit-filter-field-label" htmlFor="unit">
          Unit <span className="nc_unit-filter-required-mark">*</span>
        </label>
        <AutoComplete
          dropdown
          inputId="unit"
          className="nc_unit-filter-autocomplete"
          placeholder={unitEnabled ? 'Select a Unit' : 'Start by choosing a Roadmap'}
          value={form.unit}
          suggestions={filteredOptions.unit}
          completeMethod={e => searchItems(e, availablesOptions.unit, SearchItemsType.UNIT)}
          onChange={(e: AutoCompleteChangeEvent) => handleUnitChange(e.value)}
          field="label"
        />
      </div>
    </div>
  );

  const optionalFilters = (
    <div className="nc_unit-filter-optional">
      {miamiBrand && <span className="nc_unit-filter-optional-label">Optional filters</span>}
      {pill(
        'Class',
        <MultiSelect
          onChange={(e: MultiSelectChangeEvent) => setForm(prev => ({ ...prev, class: e.value }))}
          value={form.class}
          options={sortClassesList(optionsList.class)}
          optionLabel="sectionName"
          id="class"
          selectionLimit={1}
          filter
          disabled={!optionalFiltersEnabled}
          className={`nc_unit-filter-multiselect`}
          placeholder="Select a class"
          itemTemplate={classItemTemplate}
          selectedItemTemplate={classItemTemplate}
        />,
        form.class.length > 0,
      )}
      {pill(
        'Standards',
        <MultiSelect id="standards" {...multiSelectProps('standard', standardOptions)} />,
        isNarrowed(form.standard, standardOptions),
      )}
      {miamiBrand &&
        pill(
          'Lessons',
          <MultiSelect id="lessons" {...multiSelectProps('lesson', optionsList.lesson)} />,
          isNarrowed(form.lesson, optionsList.lesson),
        )}
    </div>
  );

  return (
    <div
      className={`col-12 nc_unit-filter-bar ${miamiBrand ? 'tw-flex-col' : 'tw-flex-row tw-items-end'}`}
      id="unitSelector"
      role="group"
      aria-label="Unit filters"
    >
      {requiredFilters}
      {!miamiBrand && <span className="nc_unit-filter-divider" aria-hidden="true" />}
      {optionalFilters}
    </div>
  );
};

export default SingleUnitFilter;
